import { Code, ConnectError } from "@connectrpc/connect";
import { useMutation, useMutationState, useQuery, useQueryClient } from "@tanstack/react-query";
import IconTriangleExclamation from "central-icons/IconTriangleExclamation";
import { useSetAtom } from "jotai";
import { type FC, useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { withDesktopDrag } from "@/components/desktop/withDesktopDrag";
import { toast } from "@/components/flexkit/toast";
import { Delayed } from "@/components/podkit/loading/Delayed";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useOnboardingCompletion } from "@/hooks/use-onboarding-completion";
import { useSegmentTrackError } from "@/hooks/use-segment";
import { setPrincipal } from "@/principal";
import { getAccountQueryKey, useGetAccount } from "@/queries/account-queries";
import { selectedRepositoryUrlAtom } from "@/routes/ona-onboarding/onboarding-state";
import { formatError } from "@/utils/errors";
import { type Organization, OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

function toPossessive(noun: string) {
    if (noun.endsWith("s")) {
        return noun + "'";
    }
    return noun + "'s";
}

export const OnaOnboardingSetupPage: FC = withDesktopDrag(() => {
    const { data: account } = useGetAccount();
    const navigate = useNavigate();
    const location = useLocation();
    const api = useGitpodAPI();
    const client = useQueryClient();
    const trackError = useSegmentTrackError();
    const setSelectedRepositoryUrl = useSetAtom(selectedRepositoryUrlAtom);

    // Extract the issuer from the returnTo query params (e.g. /?issuer=github.com)
    const loginIssuer = useMemo(() => {
        const fromSearch = location.state?.from?.search as string | undefined;
        if (fromSearch) {
            return new URLSearchParams(fromSearch).get("issuer");
        }
        return null;
    }, [location.state?.from?.search]);

    const {
        isPendingOnboarding,
        markOnboardingAsPending,
        error: onboardingCompletionError,
    } = useOnboardingCompletion();

    const {
        data: organizations,
        error: organizationsQueryError,
        isLoading: isLoadingOrganizations,
    } = useQuery({
        queryKey: [getAccountQueryKey(), "organizations"],
        queryFn: () => {
            if (!account) {
                throw new Error("Account not found");
            }
            return Promise.all(
                account.memberships.map((membership) =>
                    api.organizationService
                        .getOrganization(
                            {
                                organizationId: membership.organizationId,
                            },
                            {
                                headers: {
                                    "X-Gitpod-Principal": `user/${membership.userId}`,
                                },
                            },
                        )
                        .then(
                            (res) =>
                                ({
                                    organization: res.organization ?? null,
                                    membership,
                                }) as const,
                        ),
                ),
            );
        },
        enabled: !!account,
        throwOnError: false,
    });

    const isFirstTry = useRef(true);
    const retryCount = useRef(0);

    const { mutate: createOrganization, error: createOrganizationError } = useMutation({
        mutationKey: ["createOrganizationInOnboarding"],
        mutationFn: async ({ name }: { name: string }) => {
            const orgName = retryCount.current > 0 ? `${name} ${retryCount.current}` : name;

            let organization: Organization | null = null;
            let userId: string | null = null;
            let created = true;

            try {
                const { organization: _org, member: _member } = await api.organizationService.createOrganization({
                    name: orgName,
                    joinOrganization: true,
                    inviteAccountsWithMatchingDomain: false,
                });
                organization = _org ?? null;
                userId = _member?.userId ?? null;
            } catch (error) {
                if (error instanceof ConnectError && error.code === Code.AlreadyExists) {
                    const updatedAccount = await api.accountService.getAccount({});
                    const existingMembership = updatedAccount?.account?.memberships?.find(
                        (membership) => membership.organizationName === orgName,
                    );
                    if (existingMembership) {
                        const { organization: existingOrganization } = await api.organizationService.getOrganization(
                            {
                                organizationId: existingMembership.organizationId,
                            },
                            {
                                headers: {
                                    "X-Gitpod-Principal": `user/${existingMembership.userId}`,
                                },
                            },
                        );
                        organization = existingOrganization ?? null;
                        userId = existingMembership.userId;
                        created = false;
                    } else {
                        throw error;
                    }
                } else {
                    throw error;
                }
            }

            if (!organization) {
                throw new Error("Failed to create organization");
            }
            if (!userId) {
                throw new Error("Failed to join organization");
            }

            return { organization, principal: userId, created };
        },
        onSuccess: ({ organization, principal, created }) => {
            void client.invalidateQueries({ queryKey: getAccountQueryKey() });
            setPrincipal(principal);
            setupOrganization({ organization, created });
        },
        onError: (error) => {
            console.error("error when creating organization", error);
        },
        retry: (failureCount, error) => {
            if (error instanceof ConnectError && error.code === Code.AlreadyExists && failureCount < 3) {
                // there is an org with the same name, we increment the retry count and try again with the same name but with the index appended
                retryCount.current += 1;
                return true;
            }
            return false;
        },
    });

    const createOrganizationMutationState = useMutationState({
        filters: { mutationKey: ["createOrganizationInOnboarding"] },
    });
    const isCreatingOrganization = createOrganizationMutationState.length > 0;

    const { mutate: setupOrganization, error: setupOrganizationError } = useMutation({
        mutationFn: async (
            args: { organization: Organization; created: boolean } | { organizationId: string; created: boolean },
        ) => {
            let organization: Organization;
            if ("organization" in args) {
                organization = args.organization;
            } else {
                const { organization: org } = await api.organizationService.getOrganization({
                    organizationId: args.organizationId,
                });
                if (!org) {
                    throw new Error("Organization not found");
                }
                organization = org;
            }

            if (
                (organization.tier === OrganizationTier.FREE_ONA || organization.tier === OrganizationTier.CORE) &&
                args.created
            ) {
                await markOnboardingAsPending();
            }

            return { organization, created: args.created };
        },
        onSuccess: ({ organization, created }) => {
            if (organization.tier === OrganizationTier.FREE_ONA || organization.tier === OrganizationTier.CORE) {
                if (created || isPendingOnboarding) {
                    const locationHash = location.hash || (location.state?.from?.hash as string | undefined);
                    const repoUrlFromHash = locationHash?.substring(1);
                    if (repoUrlFromHash) {
                        setSelectedRepositoryUrl(repoUrlFromHash);
                    }
                    navigate("/onboarding/ona/connect-to-github", {
                        state: { from: location, loginIssuer },
                        replace: true,
                    });
                } else {
                    navigate("/ai", { state: { from: location }, replace: true });
                }
            } else {
                navigate("/", {
                    state: { from: location },
                    replace: true,
                });
            }
        },
        onError: (error) => {
            console.error("error when setting up organization", error);
        },
        retry: (failureCount) => failureCount <= 5,
    });

    const errorMessage = useMemo(() => {
        if (onboardingCompletionError) {
            return formatError(onboardingCompletionError);
        }
        if (organizationsQueryError) {
            return formatError(organizationsQueryError);
        }
        if (setupOrganizationError) {
            return formatError(setupOrganizationError);
        }
        if (createOrganizationError) {
            return formatError(createOrganizationError);
        }
        return "";
    }, [onboardingCompletionError, organizationsQueryError, setupOrganizationError, createOrganizationError]);

    const handleWorkspaceSetupError = useCallback(
        (error: unknown) => {
            toast({ title: "Failed to set up your workspace", description: errorMessage });
            trackError("onboarding_workspace_setup_error", error);
            console.error("failed to set up workspace", error);
        },
        [trackError, errorMessage],
    );

    useEffect(() => {
        if (account && isFirstTry.current && !isLoadingOrganizations && !isCreatingOrganization) {
            if (location.state?.organizationId) {
                // we are redirected from elsewhere with an org id specified which indicates that
                // we should put the user into that organization
                setupOrganization({
                    organizationId: location.state.organizationId as string,
                    created: false,
                });
            } else {
                // Check if the user already has a FREE_ONA or CORE organization
                const accountOrganization = organizations?.find(
                    ({ organization }) =>
                        organization?.tier === OrganizationTier.FREE_ONA ||
                        organization?.tier === OrganizationTier.CORE,
                );
                if (accountOrganization?.organization) {
                    // User already has a FREE_ONA/CORE org, use it instead of creating a new one
                    setupOrganization({ organization: accountOrganization.organization, created: false });
                } else {
                    // No existing org found, create a new one
                    isFirstTry.current = false;
                    createOrganization({
                        // append a random number to reduce the chance of a name conflict
                        name: `${toPossessive(account.name ?? account.email?.split("@")[0] ?? "My")} Workspace ${Math.floor(Math.random() * 501)}`,
                    });
                }
            }
        }
    }, [
        account,
        createOrganization,
        setupOrganization,
        errorMessage,
        isCreatingOrganization,
        location,
        organizations,
        isLoadingOrganizations,
    ]);

    useEffect(() => {
        if (errorMessage) {
            handleWorkspaceSetupError(errorMessage);
        }
    }, [errorMessage, handleWorkspaceSetupError]);

    return (
        <main
            className="flex h-full w-full select-none flex-col items-center justify-center bg-[rgb(var(--ona-white))] text-content-primary dark:bg-[rgb(var(--ona-black))]"
            style={{
                fontFamily: "var(--ona-font-family-sans)",
            }}
        >
            <Delayed wait={1500}>
                {errorMessage === "" ? (
                    <>
                        <IconSpinner className="mb-2 animate-spin" size="lg" />
                        <p className="text-xl">Getting things ready</p>
                        <p className="text-base">This will only take a moment…</p>
                    </>
                ) : (
                    <>
                        <IconTriangleExclamation className="mb-2" size={24} />
                        <p className="text-xl">Failed to set up your workspace</p>
                        <p className="text-base">{errorMessage}</p>
                    </>
                )}
            </Delayed>
        </main>
    );
});
